import {
	Component,
	OnInit,
	QueryList,
	ViewChildren,
	OnDestroy,
	AfterViewChecked,
	ChangeDetectorRef,
} from '@angular/core';
import { Store, select } from '@ngrx/store';
import {
	FormBuilder,
	FormControlName,
	FormControl,
	Validators,
	FormGroup,
} from '@angular/forms';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import {
	takeUntil,
	map,
	filter,
	mapTo,
	switchMap,
	shareReplay,
	startWith,
} from 'rxjs/operators';
import * as QuoteDetailSelectors from './+state/quote-detail.selectors';
import * as QuoteDetailActions from './+state/quote-detail.actions';
import { QuoteDetails } from './quote-detail.models';
import { FormConfig } from 'src/shared/form-models/form-cva.component';
import { QuoteDetailPartialState } from './+state/quote-detail.reducer';
import {
	Subject,
	Observable,
	merge,
	of,
	timer,
	combineLatest,
	defer,
	BehaviorSubject,
} from 'rxjs';
import { preventFlicker } from 'src/shared/utilities/custom-rxjs-operators';
import { quoteToQuoteForm } from './quote-detail.utility';
import { touchAllControls } from 'src/shared/utilities/utility-functions';
import { some, omit } from 'lodash';
import { slideInSlideOut } from 'src/shared/animations';
import { ConfirmPrevailingWageResponse, ContractRates } from 'src/shared/api';
import { SERVICE_TYPE } from 'src/shared/constants';
import { BillingDetails } from 'src/shared/billing-details/billing-details.models';

const quoteDetailFormConfig: FormConfig<QuoteDetails> = {
	generalInfo: [''],
	jobLocation: [''],
	optionalJobFields: [''],
	fieldContacts: ['', [Validators.required]],
	billingDetails: [''],
	documents: [''],
	purchaseOrderDetails: [''],
	contractedRates: [[]],
	prevailingWageInfo: [''],
	prevailingWageRates: [''],
};

@Component({
	templateUrl: './quote-detail.component.html',
	styleUrls: ['./quote-detail.component.scss'],
	animations: [slideInSlideOut],
})
export class QuoteDetailComponent
	extends SelfDestruct
	implements OnInit, AfterViewChecked, OnDestroy
{
	@ViewChildren(FormControlName) childControls: QueryList<FormControlName>;
	private _invalidApproval = new Subject<number>();
	private _invalidPrint = new Subject<number>();
	private readonly applyPrevailingWage$ = new BehaviorSubject<boolean>(false);

	form = this.fb.group(quoteDetailFormConfig) as FormGroup;
	prevailingWageToggle = new FormControl(false);
	note = new FormControl('') as FormControl;

	private readonly formValue$ = defer(
		() => this.form.valueChanges as Observable<QuoteDetails>
	).pipe(startWith(this.form.value as QuoteDetails), shareReplay(1));

	readonly hasMultipleServices$ = this.formValue$.pipe(
		map(
			(quoteForm) =>
				((quoteForm.generalInfo || {}).offeredServices || []).length >
					1 ||
				[
					...new Set(
						(quoteForm.contractedRates || [])
							.filter((r) => r.serviceType)
							.map((r) => r.serviceType.id)
					),
				].length > 1
		),
		shareReplay(1)
	);

	readonly id$ = this.store.pipe(
		select(QuoteDetailSelectors.getSelectedQuoteId)
	);

	readonly title$ = this.store.pipe(select(QuoteDetailSelectors.getTitle));

	readonly isNewRecord$ = this.id$.pipe(map((id) => id === 0));

	readonly isNonBillableJob$ = this.store.pipe(
		select(QuoteDetailSelectors.getIsNonBillableJob)
	);

	readonly isLoading$ = this.store.pipe(
		select(QuoteDetailSelectors.getLoadingStatus)
	);

	readonly isSaving$ = this.store.pipe(
		select(QuoteDetailSelectors.getIsSaving),
		preventFlicker(1000)
	);

	readonly subdivisionChoices$ = this.store.pipe(
		select(QuoteDetailSelectors.getSubdivisionOptions)
	);

	readonly optionalJobFields$ = this.store.pipe(
		select(QuoteDetailSelectors.getOptionalJobFields)
	);

	readonly hasOptionalFields$ = this.store.pipe(
		select(QuoteDetailSelectors.hasOptionalJobFields)
	);

	readonly customer$ = this.store.pipe(
		select(QuoteDetailSelectors.getCustomerForQuote)
	);

	readonly railroad$ = this.store.pipe(
		select(QuoteDetailSelectors.getRailroadForQuote)
	);

	readonly prevailingWageConfirmation$: Observable<ConfirmPrevailingWageResponse> =
		this.store.pipe(
			select(QuoteDetailSelectors.getPrevailingWageConfirmation)
		);

	readonly quoteLogListMessage$ = this.store.pipe(
		select(QuoteDetailSelectors.getQuoteLogListMessage)
	);

	readonly quoteLogs$ = this.store.pipe(
		select(QuoteDetailSelectors.getQuoteLogs)
	);

	readonly shakeApprove$ = this._invalidApproval.pipe(
		mapTo(true),
		switchMap((val) => merge(of(val), timer(1000).pipe(mapTo(false))))
	);

	readonly shakeQuoteReport$ = this._invalidPrint.pipe(
		mapTo(true),
		switchMap((val) => merge(of(val), timer(1000).pipe(mapTo(false))))
	);

	readonly canApprove$ = this.store.pipe(
		select(QuoteDetailSelectors.getCanApproveQuote)
	);

	private readonly _canPrintQuoteReport$ = this.store.pipe(
		select(QuoteDetailSelectors.canPrintQuoteReport)
	);

	readonly canPrintQuoteReport$ = combineLatest([
		this._canPrintQuoteReport$,
		this.formValue$.pipe(
			map(({ generalInfo }) => (generalInfo || {}).offeredServices || []),
			map(
				(services) =>
					services.length === 1 &&
					services[0].type.id === SERVICE_TYPE.Flagging
			)
		),
	]).pipe(
		map(([canPrint, quoteReportApplies]) => canPrint && quoteReportApplies),
		shareReplay(1)
	);

	readonly showRequiredError$ = combineLatest([
		this.form.get('fieldContacts').statusChanges,
		this._invalidApproval,
	]).pipe(
		map(([status, _]) => status !== 'VALID'),
		shareReplay(1)
	);

	constructor(
		private readonly store: Store<QuoteDetailPartialState>,
		private readonly fb: FormBuilder,
		private readonly changeDetectorRef: ChangeDetectorRef
	) {
		super();
	}

	// TODO there's some duplicated logic here around prevailing wage
	ngOnInit() {
		this.id$
			.pipe(
				takeUntil(this.destroyed),
				map((id) => id === 0)
			)
			.subscribe((isNew) =>
				isNew ? this.note.disable() : this.note.enable()
			);
		this.store
			.pipe(
				select(QuoteDetailSelectors.getSelectedQuote),
				map(quoteToQuoteForm),
				takeUntil(this.destroyed)
			)
			.subscribe((formValue) => {
				this.applyPrevailingWage$.next(false);
				this.form.patchValue(formValue);
				if (
					(formValue &&
						some(omit(formValue.prevailingWageInfo, 'id'))) ||
					some(omit(formValue.prevailingWageRates, 'id'))
				) {
					this.prevailingWageToggle.setValue(true);
					this.applyPrevailingWage$.next(true);
					if (
						formValue.prevailingWageInfo.confirmedBy ||
						formValue.prevailingWageInfo.confirmedDate
					) {
						this.prevailingWageToggle.disable();
						this.form.get('prevailingWageInfo').disable();
						this.form.get('prevailingWageRates').disable();
					}
				} else {
					this.prevailingWageToggle.setValue(false);
				}
			});

		this.prevailingWageConfirmation$
			.pipe(filter<ConfirmPrevailingWageResponse>(Boolean))
			.subscribe((confirmation) => {
				if (confirmation.confirmedBy || confirmation.confirmedDate) {
					this.prevailingWageToggle.disable();
					this.form.get('prevailingWageInfo').disable();
					this.form.get('prevailingWageRates').disable();
				}
			});

		this.store
			.pipe(
				select(QuoteDetailSelectors.getContractRates),
				filter(Boolean),
				takeUntil(this.destroyed)
			)
			.subscribe((contractedRates) => {
				this.form.get('contractedRates').patchValue(contractedRates);
			});

		this.store
			.pipe(
				select(QuoteDetailSelectors.getErrorApprovingQuote),
				filter(Boolean),
				takeUntil(this.destroyed)
			)
			.subscribe(() => {
				touchAllControls(this.childControls);
				this._invalidApproval.next(new Date().getTime());
			});

		this.applyPrevailingWage$.subscribe((value) => {
			if (value) {
				const values = this.form.get('contractedRates')
					.value as ContractRates[];
				if (!!values) {
					values.forEach((r: ContractRates) => {
						r.isPayrollFeeWeekly = true;
					});

					this.form.get('contractedRates').patchValue(values);
				}
			}
		});

		// Removing form validation for billing details on nonbillable jobs
		this.isNonBillableJob$
			.pipe(takeUntil(this.destroyed))
			.subscribe((isNonBillable) =>
				isNonBillable
					? this.form.get('billingDetails').disable()
					: this.form.get('billingDetails').enable()
			);
	}

	ngAfterViewChecked(): void {
		this.changeDetectorRef.detectChanges();
	}

	save(stay = false): void {
		this.childControls.forEach((c) => c.control.markAsPristine());
		const formValue = this.form.getRawValue();
		this.store.dispatch(
			QuoteDetailActions.saveRequested({
				shouldStayOnPage: stay,
				formValue,
			})
		);
	}

	approveQuote() {
		// Workaround to prevent submission without valid billing details
		// (for some reason this isn't prevented by the form validation
		// when creating a new quote then approving directly after without
		// refreshing the page)
		const billingDetails: BillingDetails =
			this.form.get('billingDetails').value;
		const isBillingValid =
			(!!billingDetails.billTo &&
				!!billingDetails.preferredBillingChannel) ||
			QuoteDetailSelectors.getIsNonBillableJob;

		if (this.applyPrevailingWage$.value) {
			const contractedRates = this.form.get('contractedRates')
				.value as ContractRates[];
			const invalidcontractRates = contractedRates.filter(
				(contractedRate) => contractedRate.certifiedPayrollFee === null
			);

			if (invalidcontractRates.length > 0) {
				touchAllControls(this.childControls);
				this._invalidApproval.next(new Date().getTime());
				return;
			}
		}

		if (this.form.get('contractedRates').invalid) {
			this._invalidApproval.next(new Date().getTime());
			return;
		}

		if (this.form.valid && isBillingValid) {
			const formValue = this.form.getRawValue();

			if (!this.hasValidContractedRates(formValue)) {
				this.store.dispatch(
					QuoteDetailActions.invalidContractedRates()
				);
			} else {
				this.store.dispatch(
					QuoteDetailActions.approveQuoteClicked({
						note: this.note.value,
						formValue,
					})
				);
				this.note.reset();
			}
		} else {
			touchAllControls(this.childControls);
			this._invalidApproval.next(new Date().getTime());
		}
	}

	printQuoteReport(): void {
		if (this.form.valid) {
			this.store.dispatch(QuoteDetailActions.printQuoteReportClicked());
		} else {
			touchAllControls(this.childControls);
			this._invalidPrint.next(new Date().getTime());
		}
	}

	hasValidContractedRates(formValue: QuoteDetails): boolean {
		const { generalInfo, contractedRates } = formValue;

		// CASE: A service is selected but there isn't a contracted rate for that service type
		if (generalInfo.offeredServices.length > 0) {
			const hasInvalidContractedRates =
				generalInfo.offeredServices.filter((service) => {
					return (
						contractedRates.filter(
							(contractedRate) =>
								contractedRate.serviceType.id == service.type.id
						).length === 0
					);
				}).length > 0;

			// NOTE: there is a flow in which the contractedRates list has a "template" added
			// 		 when you select a service it will create a contractRate for the selected offeredService
			// 		 that "template" will cause this logic to falsely pass
			if (hasInvalidContractedRates) {
				return false;
			}
		}

		return true;
	}

	prevailingWageToggled(isChecked: boolean): void {
		this.applyPrevailingWage$.next(isChecked);
		if (!isChecked) {
			this.form.get('prevailingWageInfo').reset(null);
			this.form.get('prevailingWageRates').reset(null);
		}
	}

	confirmPrevailingWage(): void {
		const quoteForm = this.form.value as QuoteDetails;
		this.store.dispatch(
			QuoteDetailActions.userConfirmedPrevailingWage({
				quoteForm,
			})
		);
	}

	sendNote(event: Event) {
		event.preventDefault();
		this.store.dispatch(
			QuoteDetailActions.noteSubmitted({ note: this.note.value })
		);
		this.note.reset();
	}

	ngOnDestroy() {
		this.store.dispatch(QuoteDetailActions.quoteDetailDestroyed());
		super.ngOnDestroy();
	}
}
